import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, MailOpen, Mail } from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "@/lib/api";

export default function EnquiriesTab() {
  const [enquiries, setEnquiries] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () =>
    axios.get(`${API}/enquiries`, authHeaders()).then((res) => setEnquiries(res.data)).catch(() => toast.error("Could not load enquiries"));

  useEffect(() => {
    load();
  }, []);

  const toggleRead = async (enquiry) => {
    try {
      await axios.patch(`${API}/enquiries/${enquiry.id}/read`, {}, authHeaders());
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (enquiry) => {
    if (!window.confirm(`Delete enquiry from ${enquiry.name}?`)) return;
    try {
      await axios.delete(`${API}/enquiries/${enquiry.id}`, authHeaders());
      toast.success("Enquiry deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unread = enquiries.filter((e) => !e.read).length;

  return (
    <div data-testid="admin-enquiries-tab">
      <p className="text-sm text-zinc-400 mb-6">
        {enquiries.length} enquir{enquiries.length === 1 ? "y" : "ies"} total
        {unread > 0 && <span className="text-disco-yellow font-semibold"> - {unread} unread</span>}
      </p>

      {enquiries.length === 0 ? (
        <div className="border border-white/10 py-16 text-center text-zinc-500 text-sm" data-testid="enquiries-empty-state">
          No enquiries yet. New catering requests from the website land here.
        </div>
      ) : (
        <div className="space-y-3">
          {enquiries.map((enquiry) => (
            <div
              key={enquiry.id}
              data-testid={`enquiry-row-${enquiry.id}`}
              className={`border p-5 transition-colors duration-300 ${enquiry.read ? "border-white/10 bg-disco-surface/40" : "border-disco-yellow/40 bg-disco-yellow/5"}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  data-testid={`enquiry-expand-${enquiry.id}`}
                  onClick={() => setOpenId(openId === enquiry.id ? null : enquiry.id)}
                  className="text-left min-w-0"
                >
                  <span className="flex items-center gap-2 flex-wrap">
                    {!enquiry.read && <span className="w-2 h-2 rounded-full bg-disco-yellow" data-testid={`enquiry-unread-dot-${enquiry.id}`} />}
                    <span className="text-zinc-100 font-medium">{enquiry.name}</span>
                    <span className="text-xs text-disco-teal uppercase tracking-wider">{enquiry.event_type}</span>
                  </span>
                  <span className="block text-xs text-zinc-500 mt-1">
                    {enquiry.email}
                    {enquiry.event_date && ` - Event: ${enquiry.event_date}`}
                    {enquiry.guests && ` - ${enquiry.guests} guests`}
                  </span>
                </button>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`enquiry-read-toggle-${enquiry.id}`}
                    onClick={() => toggleRead(enquiry)}
                    className="p-2 text-zinc-400 hover:text-disco-teal transition-colors"
                    aria-label={enquiry.read ? "Mark as unread" : "Mark as read"}
                  >
                    {enquiry.read ? <Mail size={15} /> : <MailOpen size={15} />}
                  </button>
                  <button
                    data-testid={`enquiry-delete-${enquiry.id}`}
                    onClick={() => remove(enquiry)}
                    className="p-2 text-zinc-400 hover:text-red-400 transition-colors"
                    aria-label="Delete enquiry"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              {openId === enquiry.id && (
                <p data-testid={`enquiry-message-${enquiry.id}`} className="mt-4 pt-4 border-t border-white/10 text-sm text-zinc-300 font-light whitespace-pre-wrap">
                  {enquiry.message}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
